import { getInitialState, updateState } from "@/utils/helpers/StoreUtil";
import DataProvider from "@/utils/helpers/DataProvider";

import { IVuexState } from "@/models/interfaces/store";
import {
  ISchool,
  IDashboard,
  ILevel,
  ITestCategory
} from "@/models/interfaces/schools";

import { AxiosError } from "axios";

import { ActionTree, MutationTree, Module } from "vuex";
import { State as RootState } from "@/store";
import { uniq, values } from "ramda";
import { ReportTypes } from "@/models/enums/report-generator";

export interface State {
  schools: IVuexState<ISchool[]>;
  dashboard: IVuexState<IDashboard>;
  levels: IVuexState<ILevel[]>;
  testCategories: IVuexState<ITestCategory[]>;
}

export const actions: ActionTree<State, RootState> = {
  async fetchSchools({ commit }) {
    commit("setSchools");

    return DataProvider.get<{ schools: ISchool[] }>("schools")
      .then(response => {
        commit("setSchools", response.data.schools);
        return response.data.schools;
      })
      .catch(error => {
        commit("setSchools", error);
        return error;
      });
  },

  async fetchLevels({ commit }, schoolsIds: string[]) {
    commit("setLevels");

    const promises = schoolsIds.map(id =>
      DataProvider.get<ILevel[]>(`schools/${id}/reports/get-levels-units`)
    );

    try {
      const response = await Promise.allSettled(promises);
      const data = response
        .map(resolvePromise =>
          resolvePromise.status === "fulfilled"
            ? values(resolvePromise.value.data)
            : []
        )
        .flat();

      commit("setLevels", uniq(data));
      return uniq(data);
    } catch (error) {
      commit("setLevels", error);
      return error;
    }
  },

  async fetchTestCategories({ commit }, params: { type: ReportTypes }) {
    commit("setTestCategories");

    try {
      const { data } = await DataProvider.get<{
        testCategories: ITestCategory[];
      }>("reports/testCategories", { params });

      commit("setTestCategories", data.testCategories);
      return data;
    } catch (error) {
      commit("setTestCategories", error);
      return error;
    }
  },

  async fetchDashboardDataForSchool({ commit }, schoolId: string) {
    commit("setDashboard");

    return DataProvider.get<{ summary: IDashboard }>(
      `schools/${schoolId}/studentSummary`
    )
      .then(response => {
        commit("setDashboard", response.data.summary);
        return response.data.summary;
      })
      .catch(error => {
        commit("setDashboard", error);
        return error;
      });
  }
};

export const mutations: MutationTree<State> = {
  setSchools(state, data?: ISchool[] | AxiosError) {
    state.schools = updateState(state.schools, data);
  },

  setLevels(state, data?: ILevel[] | AxiosError) {
    state.levels = updateState(state.levels, data);
  },

  setDashboard(state, data?: IDashboard | AxiosError) {
    state.dashboard = updateState(state.dashboard, data);
  },

  setTestCategories(state, data?: ITestCategory[] | AxiosError) {
    state.testCategories = updateState(state.testCategories, data);
  }
};

const store: Module<State, RootState> = {
  namespaced: true,
  state: {
    schools: getInitialState(),
    dashboard: getInitialState(),
    levels: getInitialState(),
    testCategories: getInitialState()
  },
  mutations,
  actions
};

export default store;
